import * as t from 'io-ts';
import {identity} from 'lodash';

import {makeApiType} from '../apiTypes';
import {makeSafePartialType} from '../types/objectTypes';
import {
  makeStashItemMergeStrategies,
  makeStashItems,
  makeStashItemType,
  makeStashItemTypes,
  makeStashServerItemTypes,
  StashItem,
} from './helpers/stashItem';
import {
  incrementStashVersionClient,
  mergeStashVersions,
  StashSourcesEnum,
  StashVersion,
} from './helpers/stashVersion';
import {
  defaultActivationPopovers,
  mergeActivationPopovers,
  RLastWriterWinsActivationPopovers,
} from './values/activationPopovers';
import {
  defaultAiComposeAssistantCustomPrompt,
  mergeAiComposeAssistantCustomPrompt,
  RAiComposeAssistantCustomPrompt,
} from './values/aiComposeAssistantCustomPrompt';
import {
  defaultAiTermsAndPrivacyAcceptance,
  mergeAiTermsAndPrivacyAcceptance,
  RAiTermsAndPrivacyAcceptance,
} from './values/aiTermsAndPrivacyAcceptance';
import {defaultBooleanValue, mergeStashBooleanValue, RStashBooleanValueBase} from './values/booleanValue';
import {
  defaultDismissedProductUpdates,
  mergeDismissedProductUpdates,
  RDismissedProductUpdates,
} from './values/dismissedProductUpdates';
import {
  defaultDismissedSuggestions,
  mergeDismissedSuggestions,
  RDismissedSuggestions,
} from './values/dismissedSuggestions';
import {defaultEmojiUsage, mergeEmojiUsage, REmojiUsage} from './values/emojiUsage';
import {
  defaultFeatureFlagOverrides,
  mergeFeatureFlagOverrides,
  RFeatureFlagOverrides,
} from './values/featureFlagOverrides';
import {
  defaultNotificationBannerPreferences,
  mergeNotificationPermissionBannerPreferences,
  RNotificationPermissionBannerPreferences,
} from './values/notificationPermissionBannerPreferences';
import {mergeNullableValue} from './values/nullableValue';
import {
  defaultPermanentlyDismissedIdsOverrides,
  mergePermanentlyDismissedIdsOverrides,
  RPermanentlyDismissedIdsOverrides,
} from './values/permanentlyDismissedIdsOverrides';
import {
  defaultMobilePrefetcherConfig,
  mergeMobilePrefetcherConfig,
  RMobilePrefetcherConfig,
} from './values/prefetcherConfig';
import {emptySetValue, makeSetValueType, mergeSetValue} from './values/setValue';
import {
  defaultUserSurveySubmissionHistory,
  mergeUserSurveySubmissionHistory,
  RUserSurveySubmissionHistory,
} from './values/userSurveySubmissionHistory';

/*
 * Runtime types.
 */

// Make sure your changes are forward and backward compatible. When in doubt, add a new field.
export const RStashValues = t.type({
  /** Whether the default video conference banner in the calendar event editor was dismissed. */
  isDefaultVideoConferenceBannerDismissed: t.boolean,
  /** Emoji usage history used to determine favorite emojis. */
  emojiUsage: REmojiUsage,
  /** When the user last saw a product announcement tip. */
  lastFlashSeenAt: t.number,
  /** Identifiers of banners, warnings, tips, etc. that have been permanently dismissed. */
  permanentlyDismissedIds: makeSetValueType(t.string),
  /** Notification permission banner preferences */
  notificationPermissionBannerPreferences: RNotificationPermissionBannerPreferences,
  /** Timestamp of the first time the user has logged in to the app */
  firstLoginTimestamp: t.union([t.number, t.null]),
  mobilePrefetcher: RMobilePrefetcherConfig,
  /** Map of dismissed product updates. Key is title:timestamp, value is dismissal timestamp */
  dismissedProductUpdates: RDismissedProductUpdates,
  /** Map of dismissed product usage suggestion timestamps. */
  dismissedSuggestions: RDismissedSuggestions,
  /** global feedback synopsis */
  synopsisFeedbackDismissedAt: makeSetValueType(t.number),
  /** Timestamp of the dismissed SmartQA feedback banner */
  smartQaReviewFeedbackDismissedAt: makeSetValueType(t.number),
  /** Conversation ids for which the SmartQA feedback banner has been dismissed */
  smartQaReviewFeedbackDismissedForConversation: makeSetValueType(t.number),
  /** Whether the user has dismissed the SmartQA AI terms banner */
  isSmartQaAiTermsBannerDismissed: RStashBooleanValueBase,
  /** Whether the user has visited Smart QA analytics page */
  hasVisitedSmartQAAnalytics: RStashBooleanValueBase,
  /** Whether we're showing absolute dates in the desktop app. */
  shouldShowAbsoluteDates: RStashBooleanValueBase,
  /** Whether we're showing absolute dates in the mobile app. */
  shouldShowAbsoluteDatesOnMobile: RStashBooleanValueBase,
  /** A list of senders for which to hide the unsubscribe banner */
  mailingListDismissedSenders: makeSetValueType(t.string),
  /** Generic model for rendering Activation related popovers. */
  activationPopovers: RLastWriterWinsActivationPopovers,
  /** Whether the user has enabled the inbox redesign. */
  hasInboxRedesignPreferenceEnabled: RStashBooleanValueBase,
  /** Whether the user has enabled the inbox improvements toggle. */
  hasInboxImprovementsToggleEnabled: RStashBooleanValueBase,
  /** Whether the user has enabled the inbox redesign on mobile (dev only). */
  hasMobileInboxRedesignPreferenceEnabled: RStashBooleanValueBase,
  /** Whether the P3 header metadata is expanded to show multiple rows. */
  isP3MetadataExpanded: RStashBooleanValueBase,
  /** Custom user system prompt for AI compose assistant. */
  aiComposeAssistantCustomPrompt: RAiComposeAssistantCustomPrompt,
  /** User survey responses */
  userSurveySubmissionHistory: RUserSurveySubmissionHistory,
  /** Whether the user has accepted the AI terms and privacy policy. */
  aiTermsAndPrivacyAcceptance: RAiTermsAndPrivacyAcceptance,

  /** Internal tools */

  /** Whether the internal toolbar should be shown. */
  shouldShowInternalToolbar: RStashBooleanValueBase,
  /** Overriden feature flags */
  featureFlagOverrides: RFeatureFlagOverrides,
  /** Whether the announcement overrides modal should be shown. */
  permanentlyDismissedIdsOverrides: RPermanentlyDismissedIdsOverrides,
} satisfies StashValueTypesTemplate);

// Used for the Redux store.
export const RStashItems = t.type(makeStashItemTypes(RStashValues.props));
// Used for reading/writing to/from local storage.
const RStashLocalStorageItems = t.intersection([
  makeSafePartialType(makeStashItemTypes(RStashValues.props), 'StashLocalStorageItems'),
  t.record(t.string, makeStashItemType(t.unknown)),
]);
// Used for reading from the server / private API.
const RStashServerItems = makeSafePartialType(
  makeStashServerItemTypes(RStashValues.props),
  'StashServerItems',
);
// Used for writing to the server / private API.
export const RStashServerValues = t.partial(RStashValues.props);

/*
 * Constants.
 */

export const defaultStashValues: StashValues = {
  isDefaultVideoConferenceBannerDismissed: false,
  emojiUsage: defaultEmojiUsage,
  lastFlashSeenAt: 0,
  permanentlyDismissedIds: emptySetValue,
  notificationPermissionBannerPreferences: defaultNotificationBannerPreferences,
  firstLoginTimestamp: null,
  mobilePrefetcher: defaultMobilePrefetcherConfig,
  dismissedProductUpdates: defaultDismissedProductUpdates,
  dismissedSuggestions: defaultDismissedSuggestions,
  synopsisFeedbackDismissedAt: emptySetValue,
  smartQaReviewFeedbackDismissedAt: emptySetValue,
  smartQaReviewFeedbackDismissedForConversation: emptySetValue,
  isSmartQaAiTermsBannerDismissed: defaultBooleanValue,
  hasVisitedSmartQAAnalytics: defaultBooleanValue,
  shouldShowAbsoluteDates: defaultBooleanValue,
  shouldShowAbsoluteDatesOnMobile: defaultBooleanValue,
  mailingListDismissedSenders: emptySetValue,
  activationPopovers: defaultActivationPopovers,
  hasInboxRedesignPreferenceEnabled: defaultBooleanValue,
  hasInboxImprovementsToggleEnabled: defaultBooleanValue,
  hasMobileInboxRedesignPreferenceEnabled: defaultBooleanValue,
  isP3MetadataExpanded: defaultBooleanValue,
  aiComposeAssistantCustomPrompt: defaultAiComposeAssistantCustomPrompt,
  userSurveySubmissionHistory: defaultUserSurveySubmissionHistory,
  aiTermsAndPrivacyAcceptance: defaultAiTermsAndPrivacyAcceptance,

  /** Internal tools */

  shouldShowInternalToolbar: defaultBooleanValue,
  featureFlagOverrides: defaultFeatureFlagOverrides,
  permanentlyDismissedIdsOverrides: defaultPermanentlyDismissedIdsOverrides,
};

export const defaultStashItems: StashItems = makeStashItems(defaultStashValues);

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const allStashKeys = Object.freeze(Object.keys(RStashValues.props)) as ReadonlyArray<StashKey>;

/*
 * Conflict resolution.
 */

// In case of a conflicting update, the server and client values will be merged. These merge functions must be
// idempotent, commutative, and associative. For complex values, consider using `mergeLastWriterWinsValue`.
const stashValueMergeStrategies: StashValueMergeStrategies = {
  isDefaultVideoConferenceBannerDismissed: (left, right) => left || right,
  emojiUsage: mergeEmojiUsage,
  lastFlashSeenAt: Math.max,
  permanentlyDismissedIds: mergeSetValue,
  notificationPermissionBannerPreferences: mergeNotificationPermissionBannerPreferences,
  firstLoginTimestamp: mergeNullableValue(Math.min),
  mobilePrefetcher: mergeMobilePrefetcherConfig,
  dismissedProductUpdates: mergeDismissedProductUpdates,
  dismissedSuggestions: mergeDismissedSuggestions,
  synopsisFeedbackDismissedAt: mergeSetValue,
  smartQaReviewFeedbackDismissedAt: mergeSetValue,
  smartQaReviewFeedbackDismissedForConversation: mergeSetValue,
  isSmartQaAiTermsBannerDismissed: mergeStashBooleanValue,
  hasVisitedSmartQAAnalytics: mergeStashBooleanValue,
  shouldShowAbsoluteDates: mergeStashBooleanValue,
  shouldShowAbsoluteDatesOnMobile: mergeStashBooleanValue,
  mailingListDismissedSenders: mergeSetValue,
  activationPopovers: mergeActivationPopovers,
  hasInboxRedesignPreferenceEnabled: mergeStashBooleanValue,
  hasInboxImprovementsToggleEnabled: mergeStashBooleanValue,
  hasMobileInboxRedesignPreferenceEnabled: mergeStashBooleanValue,
  isP3MetadataExpanded: mergeStashBooleanValue,
  aiComposeAssistantCustomPrompt: mergeAiComposeAssistantCustomPrompt,
  userSurveySubmissionHistory: mergeUserSurveySubmissionHistory,
  aiTermsAndPrivacyAcceptance: mergeAiTermsAndPrivacyAcceptance,

  /** Internal tools */

  shouldShowInternalToolbar: mergeStashBooleanValue,
  featureFlagOverrides: mergeFeatureFlagOverrides,
  permanentlyDismissedIdsOverrides: mergePermanentlyDismissedIdsOverrides,
};

export const stashMergeStrategies: StashItemMergeStrategies =
  makeStashItemMergeStrategies(stashValueMergeStrategies);

/*
 * Types.
 */

// For now, only support types that can be safely serialized into JSON.
type StashValueTypesTemplate = {[key: string]: t.Type<any, JsonValue, unknown>};
type JsonValue = string | number | boolean | null | JsonArray | JsonObject;
type JsonArray = ReadonlyArray<JsonValue>;
type JsonObject = {readonly [key: string]: JsonValue};

type StashValueTypes = typeof RStashValues.props;

export type StashKey = keyof StashValueTypes;
export type StashValue<K extends StashKey> = t.TypeOf<StashValueTypes[K]>;
export type StashValues = t.TypeOf<typeof RStashValues>;
export type StashItems = t.TypeOf<typeof RStashItems>;
export type StashLocalStorageItems = t.TypeOf<typeof RStashLocalStorageItems>;
export type StashServerItems = t.TypeOf<typeof RStashServerItems>;

interface MergeStrategy<T> {
  (left: T, right: T): T;
}

type StashValueMergeStrategies = {
  [K in StashKey]: MergeStrategy<StashValue<K>> | undefined;
};

type StashItemMergeStrategies = {
  [K in StashKey]: MergeStrategy<StashItem<StashValue<K>>>;
};

/*
 * API types.
 */

export const ApiStashLocalStorage = makeApiType('StashLocalStorage', RStashLocalStorageItems, identity);
export const ApiStashServer = makeApiType('StashServer', RStashServerItems, identity);
export const ApiStashPush = makeApiType('StashPush', t.type({items: RStashServerItems}), identity);

/*
 * Helpers.
 */

export function mergeStashItems(items: StashItems, otherItems: Partial<StashItems>): StashItems {
  let resultItems = items;

  allStashKeys.forEach(<K extends StashKey>(key: K) => {
    const otherItem = otherItems[key];

    // Don't do anything if the other item doesn't exist.
    if (!otherItem) {
      return;
    }

    const item = items[key];
    const mergedItem = stashMergeStrategies[key](item, otherItem);

    // Don't do anything if the other item is the same after merging.
    if (item === mergedItem) {
      return;
    }

    // TODO: Figure out why type checking is not working without the `satisfies` keyword.
    resultItems = {
      ...resultItems,
      [key]: mergedItem satisfies StashItem<StashValue<K>>,
    };
  });

  return resultItems;
}

export type StashVersions = {
  [key in StashKey]?: StashVersion;
};

export function mergeStashServerItems(
  items: StashItems,
  sentVersions: StashVersions,
  receivedServerItems: Partial<StashServerItems>,
): Partial<StashItems> {
  let resultItems: Partial<StashItems> = {};

  for (const key of allStashKeys) {
    const receivedServerItem = receivedServerItems[key];
    if (!receivedServerItem) {
      continue;
    }

    const {serverVersion} = items[key];
    const sentVersion = sentVersions[key];

    const receivedValue = receivedServerItem.value;
    const receivedVersion: StashVersion = {
      [StashSourcesEnum.CLIENT]: Math.max(
        // We already knew the server had this version.
        serverVersion[StashSourcesEnum.CLIENT],
        // This version was sent to the server.
        sentVersion ? sentVersion[StashSourcesEnum.CLIENT] : 0,
      ),
      [StashSourcesEnum.SERVER]: receivedServerItem.version,
    };

    resultItems = {
      ...resultItems,
      [key]: {
        value: receivedValue,
        version: receivedVersion,
        // Merge our knowledge of what has been synchronized to the server.
        serverVersion: mergeStashVersions(serverVersion, receivedVersion),
      } satisfies StashItem<unknown>,
    };
  }

  return resultItems;
}

export function updateStashItems<K extends StashKey>(
  items: StashItems,
  key: K,
  value: StashValue<K>,
): StashItems {
  const item = items[key];
  const mergeValue = stashValueMergeStrategies[key];
  const mergedValue = mergeValue ? mergeValue(item.value, value) : value;

  // Don't do anything if the value is the same after merging.
  if (item.value === mergedValue) {
    return items;
  }

  // TODO: Figure out why type checking is not working without the `satisfies` keyword.
  return {
    ...items,
    [key]: {
      value: mergedValue,
      version: incrementStashVersionClient(item.version),
      serverVersion: item.serverVersion,
    } satisfies StashItem<StashValue<typeof key>>,
  };
}
